import * as Sentry from "@sentry/nextjs";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";

import { useErrorToaster } from "src/common/components/ErrorDetails";
import { client, endpoint } from "src/lib/bridgeClient";
import { SERVER_COMM_ERROR } from "src/lib/error";

export type CreateOrgTemplateDTO = {
  template_name: string;
  workflow_name: string;
  blueprint_id: number;
  head_node_instance_type: string;
  compute_node_instance_type: string;
  is_gpu: boolean;
  scheduler_id: number;
  tags: string;
};

export type CreateTemplateDTO = CreateOrgTemplateDTO & {
  volume_id?: string;
};

export const postTemplate = async (client: Client, template: CreateTemplateDTO) => {
  return await client(endpoint.myWorkflowTemplates, "POST", template);
};

export const postOrgTemplate = async (client: Client, template: CreateOrgTemplateDTO) => {
  return await client(endpoint.myOrgWorkflowTemplates, "POST", template);
};

export const useCreateTemplate = (isOrgTemplate: boolean) => {
  const { mutate } = useSWRConfig();
  const toastError = useErrorToaster();

  return async (template: CreateTemplateDTO): Promise<AxiosResponse> => {
    let response: AxiosResponse;
    try {
      if (isOrgTemplate) {
        response = await postOrgTemplate(client, template);
        mutate(endpoint.myOrgWorkflowTemplates);
      } else {
        response = await postTemplate(client, template);
        mutate(endpoint.myWorkflowTemplates);
      }

      toast.success(`Template created: ${template.template_name}`);
    } catch (error) {
      if (error?.response?.data?.error) {
        toastError(`Failed to create template: ${template.template_name}`, error?.response?.data?.error);
      } else {
        toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
        Sentry.captureException(error);
      }

      return error.response;
    }

    return response;
  };
};
